'use client';
import React from 'react';
import { useEnterAuth } from '@enter/auth';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { LoginWithInvitedCodeModal, useReferralLoginCompletion } from '@/features/referral';
import { EnterBrandPageLoading } from '@enter/business-components/components/enter-brand-page-loading';
import { useHomeCreation } from '@/features/home/hooks/use-home-creation';
import { useContactModal } from '@/features/home/hooks/use-contact-modal';
import { shouldShowHomePageLoading } from '@/features/home/model/loading-state';
import { HomeView } from '@/features/home/view';
import { LandingV2Header } from '@/features/home/components/landing-v2/landing-v2-header';
import { Footer } from '@/components/site-footer';
import { LandingV2Prompt } from '@/features/home/components/landing-v2/landing-v2-prompt';
import { LandingV2TrackingProvider } from '@/features/home/components/landing-v2/landing-v2-tracking';
import { BookingModal } from '@/features/home/components/contact-us/booking-modal';

import { useEnterApi } from '@enter/api';
import { useToast } from '@/contexts/toast';
import { markLandingPageReady } from '@/lib/datadog/landing-page-ready';

const onHeaderReady = () => markLandingPageReady('header');
const onPromptReady = () => markLandingPageReady('prompt');

export const HomePage: React.FC = () => {
    const tHero = useTranslations('hero');
    const { isAuthenticated, isLoading, user } = useEnterAuth();
    const searchParams = useSearchParams();
    const api = useEnterApi();
    const { showSuccess, showInfo } = useToast();
    const { open: contactModalOpen, setOpen: setContactModalOpen } = useContactModal();

    const { selectedModel, handleBuildAction, handleSubmit } = useHomeCreation(isAuthenticated);

    const { shouldShowLoading } = useReferralLoginCompletion({
        isAuthenticated,
        isLoading,
        userSub: user?.sub,
        api,
        showSuccess,
        showInfo,
    });
    const isAuthCallbackLoading =
        isLoading && searchParams.has('code') && searchParams.has('state');
    const showPageLoading = shouldShowHomePageLoading({
        redirectLoading: shouldShowLoading,
        isAuthCallbackLoading,
    });

    if (showPageLoading) {
        return (
            <EnterBrandPageLoading
                className="min-h-screen bg-background"
                text=""
            />
        );
    }

    return (
        <LandingV2TrackingProvider>
            {({ getNavigationTrackingProps }) => (
                <HomeView
                    semanticHeading={tHero('seoTitle')}
                    onBuildAction={handleBuildAction}
                    onGetStarted={() => handleBuildAction('apps', 'footer')}
                    onPortalClick={() => handleBuildAction('apps', 'hero-portal')}
                    header={({ introComplete }: { introComplete: boolean }) => (
                        <LandingV2Header introComplete={introComplete} getTrackingContext={getNavigationTrackingProps} onReady={onHeaderReady} />
                    )}
                    footer={<Footer trackingSurface="landing_v2" getTrackingContext={getNavigationTrackingProps} />}
                    prompt={(
                        <LandingV2Prompt
                            onSubmit={handleSubmit}
                            selectedModel={selectedModel}
                            onReady={onPromptReady}
                        />
                    )}
                    announcement={null}
                >
                <LoginWithInvitedCodeModal />
                <BookingModal
                    open={contactModalOpen}
                    onOpenChange={setContactModalOpen}
                    trackingSurface="landing_v2"
                />
                </HomeView>
            )}
        </LandingV2TrackingProvider>
    );
};
